import {
  clearDurableStateValue,
  QUICK_FILL_ONBOARDING_STORAGE_KEY,
  QUICK_FILL_TOUR_PROGRESS_STORAGE_KEY,
  setDurableStateValue,
} from '../../../shared/lib/durableState/durableState';

const QUICK_FILL_ONBOARDING_KEY = QUICK_FILL_ONBOARDING_STORAGE_KEY;
const QUICK_FILL_TOUR_PROGRESS_KEY = QUICK_FILL_TOUR_PROGRESS_STORAGE_KEY;

// Reads hit localStorage (restored from the plugin-mirrored durable store at
// bootstrap); writes go through the durable mirror so the "I've already seen
// the tour" flag survives WebView2 cache purges / runtime reinstalls. Earlier
// sessionStorage-based impl re-popped the auto-tour every fresh session —
// confirmed UX bug. Per-page wrappers fall back gracefully on storage failures
// (Safari private mode, disabled storage, etc.) so the worst case is "tour
// shows once more than necessary," never a crash.
const quickFillStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const hasSeenQuickFillOnboarding = () => {
  return quickFillStorage()?.getItem(QUICK_FILL_ONBOARDING_KEY) === '1';
};

export const markQuickFillOnboardingSeen = () => {
  try {
    setDurableStateValue(QUICK_FILL_ONBOARDING_KEY, '1');
  } catch {
    // Ignore storage failures; onboarding can reopen next visit.
  }
};

export const readQuickFillTourProgress = (stepCount: number): number | null => {
  if (stepCount <= 1) {
    return null;
  }

  const storedValue = quickFillStorage()?.getItem(QUICK_FILL_TOUR_PROGRESS_KEY);
  const parsedStep = storedValue ? Number.parseInt(storedValue, 10) : Number.NaN;

  return Number.isInteger(parsedStep) && parsedStep > 0 && parsedStep < stepCount - 1
    ? parsedStep
    : null;
};

export const writeQuickFillTourProgress = (stepIndex: number, stepCount: number) => {
  try {
    if (stepIndex > 0 && stepIndex < stepCount - 1) {
      setDurableStateValue(QUICK_FILL_TOUR_PROGRESS_KEY, String(stepIndex));
      return;
    }

    clearDurableStateValue(QUICK_FILL_TOUR_PROGRESS_KEY);
  } catch {
    // Ignore storage failures; the tour can still run normally.
  }
};

export const clearQuickFillTourProgress = () => {
  try {
    clearDurableStateValue(QUICK_FILL_TOUR_PROGRESS_KEY);
  } catch {
    // Ignore storage failures; the tour can still run normally.
  }
};

/**
 * Wipes the "have I seen the QuickFill onboarding" flag so the next mount
 * auto-opens the tour again. Useful for a future "Reset tour state" menu
 * action and for tests; not invoked automatically.
 */
export const resetQuickFillOnboardingState = () => {
  try {
    clearDurableStateValue(QUICK_FILL_ONBOARDING_KEY);
    clearDurableStateValue(QUICK_FILL_TOUR_PROGRESS_KEY);
  } catch {
    // Ignore storage failures.
  }
};
